import { twMerge } from 'tailwind-merge';

import { WheelKind } from 'models/wheel-of-fortune/i-wheel-of-fortune';

interface WheelOfFortuneSwitchProps {
  value: WheelKind;
  onChange: (kind: WheelKind) => void;
  /** Spins held per wheel, shown on each tab so the choice is informed. */
  regularSpins?: number;
  vipSpins?: number;
  /** Locks the selector — used mid-spin, when changing wheels would corrupt the result. */
  disabled?: boolean;
}

const TABS: { kind: WheelKind; label: string; activeClass: string }[] = [
  // The active tab is filled in its own wheel's colour: green like every other primary
  // action for the regular wheel, gold for VIP, matching the wheel it selects.
  { kind: 'regular', label: 'Regular', activeClass: 'wof-btn--primary' },
  { kind: 'vip', label: 'VIP', activeClass: 'wof-btn--amber' },
];

/**
 * Wheel selector — a segmented pill built from the same button system as every other
 * action in this modal (see WheelOfFortuneModal.scss, which mirrors Bid2Earn's `.btn`),
 * so it does not read as a different control language from the buttons beside it.
 */
const WheelOfFortuneSwitch = ({
  value,
  onChange,
  regularSpins,
  vipSpins,
  disabled = false,
}: WheelOfFortuneSwitchProps) => (
  <div role="tablist" aria-label="Choose a wheel" className="wof-switch">
    {TABS.map(({ kind, label, activeClass }) => {
      const isActive = value === kind;
      const spins = kind === 'vip' ? vipSpins : regularSpins;

      return (
        <button
          key={kind}
          type="button"
          role="tab"
          aria-selected={isActive}
          // Only the tab you could move TO is disabled: clicking the active one is
          // already a no-op, and dimming it would make the current wheel look unavailable.
          disabled={disabled && !isActive}
          onClick={() => onChange(kind)}
          className={twMerge('wof-btn wof-switch__tab', isActive ? activeClass : 'wof-switch__tab--idle')}
        >
          {label}
          {typeof spins === 'number' && (
            <span className={twMerge('wof-switch__count', isActive && 'wof-switch__count--active')}>{spins}</span>
          )}
        </button>
      );
    })}
  </div>
);

export default WheelOfFortuneSwitch;
